import { ContentState, convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import { fromJS, Iterable, List, Map } from 'immutable';
import { findMatchingSchema } from './discriminator.js';
import { createResolveRef } from './refs.js';

function deserializeEditorState(data) {
  let contentState;
  if (typeof data === 'string') {
    contentState = ContentState.createFromText(data);
  } else {
    contentState = convertFromRaw(Iterable.isIterable(data) ? data.toJS() : data);
  }

  return EditorState.createWithContent(contentState);
}

function serializeEditorState(editorState) {
  return convertToRaw(editorState.getCurrentContent());
}

function serializerFactory(convert) {
  return (schema, data, toPojo = false) => {
    const resolveRef = createResolveRef(schema);

    // eslint-disable-next-line complexity
    const transform = (schema, data, toPojo = false) => {
      if (data === null || data === undefined || schema === undefined) {
        return data;
      }

      const fromImmutable = Iterable.isIterable(data);

      const ref = schema.get('$ref');
      if (ref) {
        schema = resolveRef(schema);
      }

      const oneOf = schema.get('oneOf');
      if (oneOf) {
        const matchingSchema = findMatchingSchema(resolveRef, schema, data);
        if (matchingSchema) {
          schema = resolveRef(matchingSchema);
        }
      }

      const type = schema.get('type');
      if (type === 'object' || oneOf !== undefined) {
        if (schema.get('draftjs') || schema.get('@tag') === 'draftjs') {
          return convert(data);
        }

        const properties = schema.get('properties');
        if (properties) {
          const result = {};
          properties.forEach((propSchema, name) => {
            const value = fromImmutable ? data?.get(name) : data?.[name];
            if (value !== undefined) {
              result[name] = transform(propSchema, value, toPojo);
            }
          });

          return toPojo ? result : Map(result);
        }

        if (fromImmutable) {
          return toPojo ? data?.toJS() : data;
        }

        return toPojo ? data : fromJS(data);
      }

      if (type === 'array') {
        const items = schema.get('items');
        if (items) {
          const mapped = data?.map((item) => transform(items, item, toPojo));
          if (Iterable.isIterable(data)) {
            return toPojo ? mapped.toArray() : mapped;
          }

          return toPojo ? mapped : List(mapped);
        }
      }

      return toPojo && Iterable.isIterable(data) ? data.toJS() : data;
    };

    return transform(schema, data, toPojo);
  };
}

export const serializeData = serializerFactory(serializeEditorState);
export const deserializeData = serializerFactory(deserializeEditorState);

export function toPojo(obj, transform) {
  const convert = (obj) => {
    if (Iterable.isKeyed(obj)) {
      return obj.toSeq().map(convert).toObject();
    }

    if (Iterable.isIndexed(obj)) {
      return obj.toSeq().map(convert).toArray();
    }

    return transform ? transform(obj) : obj;
  };

  return convert(obj);
}
